import React, { useState, useEffect } from 'react';
import useFlaresSmootheningStore from '../../stores/useFlaresSmootheningStore';
import styles from './ToolCard.module.css';
import DynamicSlider from '../ui/DynamicSlider';
import DeleteIcon from '../../assets/icons/delete.svg?react';
import { useToolManager } from '../../hooks/useToolManager';
import { TOOLS_CONFIG } from '../../constants/tools';
import useEditorStore from '../../stores/useEditorStore';

const FlaresSmoothningToolCard = () => {
  const smoothningState = useFlaresSmootheningStore((state) => state.smoothning);
  const setSmoothning = useFlaresSmootheningStore((state) => state.setSmoothning);
  const isApplied = useFlaresSmootheningStore((state) => state.isSmootheningApplied);
  const undoCommand = useFlaresSmootheningStore((state) => state.currentSmootheningCommand);
  const isAppliedFromDraft = useFlaresSmootheningStore((state) => state.isSmootheningAppliedFromDraft);
  const { run } = useToolManager();

  const [zones, setZones] = useState(smoothningState.zones);
  const [nextId, setNextId] = useState(
    zones.length > 0 ? Math.max(...zones.map((z) => z.id)) + 1 : 1
  );

  useEffect(() => {
    setZones(smoothningState.zones);
    if (smoothningState.zones.length > 0) {
      setNextId(Math.max(...smoothningState.zones.map((z) => z.id)) + 1);
    }
  }, [smoothningState.zones]);

  const colors = ['#f97316', '#ec4899', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#3b82f6'];

  const addNewZone = () => {
    const newZone = {
      id: nextId,
      name: `Zone ${nextId.toString().padStart(2, '0')}`,
      smootheningValue: 1,
      isExpanded: zones.length === 0,
      color: colors[(nextId - 1) % colors.length],
    };

    const newZones = [...zones, newZone];
    setZones(newZones);
    setNextId(nextId + 1);
    setSmoothning({ zones: newZones });
  };

  const deleteZone = (id) => {
    const newZones = zones.filter((zone) => zone.id !== id);
    setZones(newZones);

    if (newZones.length > 0 && !newZones.some((zone) => zone.isExpanded)) {
      const updatedZones = newZones.map((zone, index) =>
        index === 0 ? { ...zone, isExpanded: true } : zone
      );
      setZones(updatedZones);
      setSmoothning({ zones: updatedZones });
    } else {
      setSmoothning({ zones: newZones });
    }
  };

  const toggleZone = (id) => {
    const updatedZones = zones.map((zone) => ({
      ...zone,
      isExpanded: zone.id === id ? !zone.isExpanded : false,
    }));
    setZones(updatedZones);
    setSmoothning({ zones: updatedZones });
  };

  const updateSmootheningValue = (id, value) => {
    const updatedZones = zones.map((zone) =>
      zone.id === id ? { ...zone, smootheningValue: parseFloat(value) || 0 } : zone
    );
    setZones(updatedZones);
    setSmoothning({ zones: updatedZones });
  };

  const applySmoothening = () => {
    if (!isApplied) {
      run({ tool: TOOLS_CONFIG.MAIN_PROCESS.FLARES.main_flares_smoothning.id, mode: 'apply' });
    } else if (undoCommand) {
      const { resetHistoryUpTo } = useEditorStore.getState();
      resetHistoryUpTo(undoCommand);
      undoCommand.undo();
    }
  };

  const isLoadedFromDraft = isApplied && isAppliedFromDraft;

  return (
    <div className='flex flex-col absolute left-[10px] top-[10px]'>
      <div className='bg-white rounded-md p-2 pb-3 shadow-[0_2px_8px_rgba(0,0,0,0.15)] w-[264px] z-[1000] max-h-[70vh] overflow-y-auto border border-gray-200'>
        <div className={styles.toolCardHeader}>
          <h3>Flares Smoothening</h3>
          {!isApplied ? (
            zones.length === 0 ? <p>Create smoothening zones</p> : <p>Fine-tune the flares</p>
          ) : (
            <p>Smoothening Applied</p>
          )}
        </div>

        {!isApplied && zones.length > 0 && (
          <div className={styles.zonesContainer}>
            <DynamicSlider
              min={0.25}
              max={3}
              step={0.25}
              value={smoothningState.brushRadius}
              onChange={(value) => setSmoothning({ brushRadius: value })}
              label='Brush Radius'
              layout='inline'
              className='my-4 px-2'
              labelClassName='text-gray-500'
              thumbColor='#ffb900'
            />

            <div className='max-h-92 overflow-y-auto pr-2'>
              {zones.map((zone) => (
                <div key={zone.id} className={styles.zoneContainer}>
                  {!zone.isExpanded ? (
                    <div
                      className={styles.zoneHeaderCollapsed}
                      onClick={() => toggleZone(zone.id)}
                    >
                      <div
                        className={styles.zoneIndicator}
                        style={{ backgroundColor: zone.color }}
                      ></div>
                      <span className={styles.zoneName}>{zone.name}</span>
                    </div>
                  ) : (
                    <div className={styles.zoneExpanded}>
                      <div className={styles.zoneHeaderExpanded}>
                        <div className={styles.zoneHeaderLeft}>
                          <div
                            className={styles.zoneIndicator}
                            style={{ backgroundColor: zone.color }}
                          ></div>
                          <span className={styles.zoneName}>{zone.name}</span>
                        </div>
                        <div className={styles.zoneHeaderRight}>
                          <button
                            className={styles.deleteButton}
                            onClick={() => deleteZone(zone.id)}
                            title="Delete zone"
                          >
                            <DeleteIcon className='w-5 h-5' />
                          </button>
                        </div>
                      </div>

                      <div className={styles.zoneContent}>
                        <div className={styles.sliderContainer}>
                          <DynamicSlider
                            min={1}
                            max={80}
                            value={zone.smootheningValue}
                            onChange={(value) => updateSmootheningValue(zone.id, value)}
                            label='Smoothning'
                            layout='inline'
                            labelClassName='font-medium'
                            activeTrackClassName='bg-gradient-to-r from-purple-500 to-purple-300'
                            valueClassName='text-purple-500 font-medium'
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={styles.toolCardFooter}>
          {!isApplied ? (
            <button className={styles.addButton} onClick={addNewZone}>
              Add New Zone
            </button>
          ) : (
            <button
              className={`px-2 py-1 rounded-md min-w-20 text-white ${
                isLoadedFromDraft
                  ? 'bg-gray-400 cursor-not-allowed opacity-60'
                  : 'bg-red-400 cursor-pointer hover:bg-red-500 hover:text-white'
              }`}
              onClick={isLoadedFromDraft ? undefined : applySmoothening}
              disabled={isLoadedFromDraft}
              title=
                {isLoadedFromDraft
                  ? 'Cannot unapply smoothening loaded from draft'
                  : 'Unapply smoothening'}
            >
              Unapply
            </button>
          )}
        </div>
      </div>

      {!isApplied && zones.length > 0 && (
        <div className='flex items-center justify-between mt-4 px-2'>
          <label className='flex items-center cursor-pointer select-none ml-1'>
            <input
              type='checkbox'
              checked={smoothningState.previewVisible}
              onChange={() => setSmoothning({ previewVisible: !smoothningState.previewVisible })}
              className='sr-only'
            />
            <div
              className={`w-10 h-6 rounded-full transition-colors duration-200 relative ${
                smoothningState.previewVisible ? 'bg-purple-600' : 'bg-gray-300'
              }`}
            >
              <div
                className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 z-10 ${
                  smoothningState.previewVisible ? 'translate-x-4' : 'translate-x-0'
                }`}
              ></div>
            </div>
            <span className='ml-3 text-sm font-medium text-gray-700'>Show Preview</span>
          </label>

          <button
            className='px-2 py-1 rounded-md ml-2 min-w-22 text-white text-lg cursor-pointer bg-purple-600 hover:text-white hover:bg-purple-500'
            onClick={applySmoothening}
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
};

export default FlaresSmoothningToolCard;

